import {
  Indicator,
  IndicatorLabel,
  IndicatorValue,
  IndicatorsWrapper,
  InfoText,
  StyledKeyIndicators,
} from './styles/KeyIndicators.styles'
import type { BaseSlice } from '../../comp_reusable/report_page_universal/stores/baseReportStore'
import {
  parseISO,
  differenceInYears,
  differenceInMonths,
  differenceInDays,
} from 'date-fns'

type BaseStoreHook<T extends BaseSlice> = (selector: (state: T) => any) => any

interface KeyIndicatorsProps {
  store?: BaseStoreHook<BaseSlice>
}

const calculateDuration = (from: string, to: string): string => {
  const fromDate = parseISO(from)
  const toDate = parseISO(to)

  const years = differenceInYears(toDate, fromDate)
  if (years > 0) {
    return `${years} year${years > 1 ? 's' : ''}`
  }

  const months = differenceInMonths(toDate, fromDate)
  if (months > 0) {
    return `${months} month${months > 1 ? 's' : ''}`
  }

  const days = differenceInDays(toDate, fromDate)
  const weeks = Math.floor(days / 7)
  if (weeks > 0) {
    return `${weeks} week${weeks > 1 ? 's' : ''}`
  }

  return `${days} day${days > 1 ? 's' : ''}`
}

export function KeyIndicators({ store }: KeyIndicatorsProps) {
  const initialReportData = store
    ? store((state: BaseSlice) => state.anyReport)
    : null

  const keyIndicators = initialReportData?.key_indicators
  const parameters = initialReportData?.artifact?.optimize_config
  const timeframe = initialReportData?.artifact?.timeframe

  if (!keyIndicators) {
    return null
  }

  // Format number with thousand separators
  const formatValue = (value: number): string => {
    // locale 'de' uses dots for thousand separators
    return new Intl.NumberFormat('de', {
      maximumFractionDigits: 1,
    }).format(value)
  }

  const timePeriod = timeframe
    ? calculateDuration(timeframe.from, timeframe.to)
    : ''

  return (
    <StyledKeyIndicators>
      <IndicatorsWrapper>
        {Object.entries(keyIndicators).map(
          ([key, indicator]: [string, any]) => (
            <Indicator key={key}>
              <IndicatorLabel>{indicator.title}</IndicatorLabel>
              <IndicatorValue>{formatValue(indicator.value)}</IndicatorValue>
            </Indicator>
          ),
        )}
      </IndicatorsWrapper>
      <InfoText>
        Parameters: {parameters?.budget_type} budget. Channel level spend
        constraint +/- {parameters?.spend_constraint * 100}%. Time period{' '}
        {timePeriod} ({timeframe.from} - {timeframe.to}).
      </InfoText>
    </StyledKeyIndicators>
  )
}
